import React from "react";
import Card from "components/card";
import TimePicker from "react-time-picker";
import Button from "components/button/Button";
import DropzoneFile from "components/dropzone";
import TextField from "components/fields/TextField";
import InputField from "components/fields/InputField";
import SwitchField from "components/fields/SwitchField";
import SelectField from "components/fields/SelectField";
import NumFormatField from "components/fields/NumFormatField";
import SelectAsyncField from "components/fields/SelectAsyncField";
import { getUsers } from "api/UserAPI";
import { Roles } from "constants/roles";
import { toast } from "react-hot-toast";
import { IUsers } from "./types/users.interface";
import { useNavigate, useParams } from "react-router-dom";
import {
  Controller,
  FieldValues,
  SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form";
import { addCoupon, getCoupon, updateCoupon } from "api/CouponAPI";
import { CouponType, DiscountType } from "./types/coupons.interface";

import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

const couponTypes: {
  value: string;
  label: string;
}[] = [
  {
    value: CouponType.Purchase,
    label: "Pembelian",
  },
  {
    value: CouponType.Shipment,
    label: "Pengiriman",
  },
];

const discountTypes: {
  value: string;
  label: string;
}[] = [
  {
    value: DiscountType.Nominal,
    label: "Nominal",
  },
  {
    value: DiscountType.Percentage,
    label: "Persentase",
  },
];

const CouponForm: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [files, setFiles] = React.useState<File[]>([]);
  const [defaultTime] = React.useState<string>("00:00");
  const [defaultImg, setDefaultImg] = React.useState<string>("");
  const [users, setUsers] = React.useState<IUsers[]>([]);
  const [defaultEligibleUsers, setDefaultEligibleUsers] = React.useState<
    IUsers[]
  >([]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({});

  const watchIsPrivate = useWatch({
    control,
    name: "is_private",
  });
  const watchDiscountType = useWatch({
    control,
    name: "discount_type",
  });

  const dropFile = (files: File[]) => {
    setFiles([...files]);
  };

  const removeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const arrangeFiles = (files: File[]) => {
    setFiles([...files]);
  };

  const changeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    setIsLoading(true);

    data.minimum_transaction =
      typeof data.minimum_transaction === "string"
        ? data.minimum_transaction.replace(/[Rp.]/g, "")
        : data.minimum_transaction;

    data.discount =
      typeof data.discount === "string"
        ? data.discount.replace(/[%Rp.]/g, "")
        : data.discount;

    const start_at = new Date(
      data.start_at + " " + data.timestart_at
    ).toISOString();
    const end_at = new Date(data.end_at + " " + data.timeend_at).toISOString();

    const eligibleUsers = data?.users;
    delete data.users;

    const payload = {
      ...data,
      minimum_transaction: data?.minimum_transaction
        ? parseInt(data.minimum_transaction)
        : 0,
      discount: parseInt(data.discount),
      type: data.coupon_type.value,
      coupon_type: data.coupon_type.value,
      discount_type: data.discount_type.value,
      user_id: eligibleUsers?.map((item: any) => item.id),
      quota: parseInt(data?.quota?.toString() || "0"),
      image: files.length > 0 ? files[0] : null,
      start_at: new Date(start_at),
      end_at: new Date(end_at),
      is_active: data?.is_active,
      is_private: data?.is_private,
      name: data?.name,
      code: data?.code,
      description: data?.description,
    };

    payload.image === null && delete payload.image;

    let promise = null;

    if (!watchEligibleUsers(eligibleUsers)) return;
    else if (!watchIsPrivate) delete payload.user_id;

    if (!slug) promise = addCoupon(payload);
    else promise = updateCoupon(slug, payload);

    toast.promise(promise, {
      loading: "Menyimpan data kupon...",
      success: () => {
        navigate("/admin/coupons");
        return "Berhasil menyimpan data kupon!";
      },
      error: (err: any) =>
        err?.response?.data?.message || "Gagal menyimpan data kupon!",
    });

    setIsLoading(false);
  };

  const getDetailData = (slug: string): void => {
    const promise = getCoupon(slug);

    toast.promise(promise, {
      loading: "Mengambil data kupon...",
      success: (res) => {
        if (res.status === 200) {
          delete res.data.data.created_at;
          delete res.data.data.updated_at;
          delete res.data.data.id;
          delete res.data.data.slug;

          if (res.data.data?.users) {
            const eligibleUsers = res.data.data?.users?.map((item: IUsers) => {
              return {
                ...item,
                value: item.id,
                label: item.name,
              };
            });

            setDefaultEligibleUsers(eligibleUsers);
            setValue("users", [...eligibleUsers]);
          }
          reset({
            ...res.data.data,
            users: users.find((item) => item.id === res.data.data?.user_id),
            coupon_type: couponTypes.find(
              (item) => item.value === res.data.data.type
            ),
            discount_type: discountTypes.find(
              (item) => item.value === res.data.data.discount_type
            ),
            start_at: res.data.data.start_at.split("T")[0],
            end_at: res.data.data.end_at.split("T")[0],
            timestart_at: new Date(res?.data?.data?.start_at)
              .toString()
              .split(" ")[4],
            timeend_at: new Date(res?.data?.data?.end_at)
              .toString()
              .split(" ")[4],
          });

          if (res.data.data.image) setDefaultImg(res.data.data.image);

          return res.data.message || "Berhasil mengambil data kupon!";
        }
      },
      error: (err) => {
        console.error(err);
        return err?.response?.data?.message || "Gagal mengambil data kupon!";
      },
    });
  };

  const getCustomers = () => {
    const promise = getUsers();

    promise
      .then((res) => {
        if (res.status === 200) setUsers(res.data.data.data);
      })
      .catch((err) => {
        console.error(err);
        return (
          err?.response?.data?.message || "Gagal mengambil data pelanggan!"
        );
      });
  };

  const loadCustomerOptions = async (
    search: string,
    loadedOptions: any,
    { page }: any
  ) => {
    const res = await getUsers(search, Roles.CUSTOMER, page);
    const resJSON = [
      ...loadedOptions,
      ...res.data.data.data.map((item: IUsers) => ({
        ...item,
        value: item.id,
        label: item.name,
      })),
    ];

    return {
      options: resJSON,
      hasMore: resJSON.length >= 10,
      additional: {
        page: page + 1,
      },
    };
  };

  const handleOnInputChange = (value: IUsers[]) => {
    watchEligibleUsers(value);

    setDefaultEligibleUsers([...value]);
    setValue("users", [...value]);
  };

  const watchEligibleUsers = (users: IUsers[]) => {
    if (watchIsPrivate && users.length === 0) {
      toast("Pengguna diskon harus dipilih jika kupon terbatas!", {
        icon: "📢",
      });
      setIsLoading(false);
      return false;
    }

    return true;
  };

  React.useEffect(() => {
    getCustomers();
    if (slug) getDetailData(slug);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6 pb-3 mt-3"}>
      <header className="relative flex items-center justify-between pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Tambah Kupon
        </div>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
        <div className="mt-8">
          <div className="flex flex-col">
            <label htmlFor="images" className="label">
              Foto Kupon
            </label>
            <DropzoneFile
              text="Foto Kupon"
              accept={{
                "image/*": [".jpg", ".png", ".jpeg", ".webp"],
              }}
              maxSize={2048000}
              maxFiles={1}
              files={files}
              dropFile={(files: File[]) => dropFile(files)}
              removeFile={(files: File[]) => removeFile(files)}
              arrangeFiles={(files: File[]) => arrangeFiles(files)}
              changeFile={(files: File[]) => changeFile(files)}
            />
          </div>
          {defaultImg && (
            <div className="my-3 flex flex-col">
              <label htmlFor="images" className="label">
                Foto Lama Kupon
              </label>
              <div className="mx-auto flex h-48 w-auto flex-col items-center justify-center rounded-md border border-gray-300">
                <img
                  src={
                    defaultImg
                      ? defaultImg.includes("https://")
                        ? defaultImg
                        : `${process.env.REACT_APP_PUBLIC_URL}/` + defaultImg
                      : "https://placehold.co/600x400?text=No+Thumbnail"
                  }
                  alt="coupon"
                  className="h-full w-full rounded-md object-cover"
                />
              </div>
            </div>
          )}
          <p className="mb-3 ml-3 mt-7 font-medium text-gray-600">
            Detail Kupon
          </p>
          <div className="w-fit px-3">
            <SwitchField
              label="Aktivasi Kupon"
              desc=""
              id="is_active"
              mb="mb-3"
              mt=""
              placeholder="Aktivasi Kupon"
              register={register}
              errors={errors}
              validationSchema={{}}
            />
          </div>
          <InputField
            extra="mb-3"
            label="Nama Kupon"
            placeholder="Masukkan nama kupon"
            id="name"
            type="text"
            variant="outline"
            register={register}
            errors={errors}
            validationSchema={{
              required: "Nama Kupon harus diisi!",
            }}
          />
          <Controller
            name="coupon_type"
            control={control}
            rules={{
              required: "Tipe diskon harus diisi!",
            }}
            render={({ field }) => (
              <SelectField
                field={field}
                errors={errors}
                label="Tipe Kupon"
                id="coupon_type"
                placeholder="Pilih Tipe Kupon"
                extra="mb-3 flex-1 w-full"
                options={couponTypes}
                optionLabel="label"
                optionValue="value"
                rules={{
                  required: "Tipe Kupon harus dipilih!",
                }}
                variant="outline"
              />
            )}
          />
          <InputField
            extra="mb-3"
            label="Kode"
            placeholder="Masukkan kode kupon"
            id="code"
            type="text"
            variant="outline"
            register={register}
            errors={errors}
            validationSchema={{}}
          />
          <div className="mb-3 mt-5 flex items-center justify-start">
            <div className="w-fit px-3">
              <SwitchField
                label="Kupon Terbatas"
                desc=""
                id="is_private"
                mb="mb-3"
                mt=""
                placeholder="Kupon Terbatas"
                register={register}
                errors={errors}
                validationSchema={{}}
              />
            </div>
            {watchIsPrivate && (
              <SelectAsyncField
                loadOptions={loadCustomerOptions}
                onInputChange={(value: any) => handleOnInputChange(value)}
                value={defaultEligibleUsers}
                id="users"
                label="Pengguna Diskon"
                placeholder="Pilih Pengguna diskon"
                extra="mb-3 flex-1 w-full"
                isMulti={true}
                errors={errors}
                rules={{
                  required: "Pengguna diskon harus dipilih!",
                }}
              />
            )}
          </div>
          <Controller
            name="minimum_transaction"
            control={control}
            rules={{}}
            render={({ field }) => (
              <NumFormatField
                field={field}
                extra="mb-3"
                decimalSeparator=","
                thousandSeparator="."
                errors={errors}
                rules={{}}
                label="Transaksi Minimum"
                id="minimum_transaction"
                placeholder="Masukkan transaksi minimum"
                variant="outline"
                prefix="Rp"
              />
            )}
          />
          <InputField
            extra="mb-3 flex-1 w-full"
            label="Kuota"
            placeholder="Masukkan kuota kupon"
            id="quota"
            type="number"
            variant="outline"
            minLength={1}
            register={register}
            errors={errors}
            validationSchema={{}}
          />
          <div className="flex w-full gap-3">
            <Controller
              name="discount_type"
              control={control}
              rules={{
                required: "Bentuk diskon harus diisi!",
              }}
              render={({ field }) => (
                <SelectField
                  field={field}
                  errors={errors}
                  label="Bentuk Diskon"
                  id="discount_type"
                  placeholder="Pilih Bentuk Diskon"
                  extra="mb-3 flex-1 w-full"
                  options={discountTypes}
                  optionLabel="label"
                  optionValue="value"
                  rules={{
                    required: "Bentuk Diskon harus dipilih!",
                  }}
                  variant="outline"
                />
              )}
            />
            <Controller
              name="discount"
              control={control}
              rules={{
                required: "Diskon harus diisi!",
              }}
              render={({ field }) => (
                <NumFormatField
                  field={field}
                  extra="mb-3 flex-1 w-full"
                  decimalSeparator=","
                  thousandSeparator={
                    watchDiscountType?.value === DiscountType.Nominal ? "." : ""
                  }
                  errors={errors}
                  rules={{
                    required: "Diskon harus diisi!",
                  }}
                  label="Diskon"
                  id="discount"
                  placeholder="Masukkan diskon"
                  variant="outline"
                  prefix={
                    watchDiscountType?.value === DiscountType.Nominal
                      ? "Rp"
                      : ""
                  }
                  suffix={
                    watchDiscountType?.value === DiscountType.Percentage
                      ? "%"
                      : ""
                  }
                />
              )}
            />
          </div>
          <div className="flex w-full gap-3">
            <div className="flex-1 flex w-full flex-col gap-3">
              <InputField
                extra="mb-3 flex-1 w-full"
                label="Tanggal dan Waktu Mulai"
                placeholder="Masukkan tanggal mulai"
                id="start_at"
                type="date"
                variant="outline"
                register={register}
                errors={errors}
                validationSchema={{
                  required: "Tanggal mulai harus diisi!",
                }}
              />
              <Controller
                name="timestart_at"
                control={control}
                defaultValue={defaultTime}
                rules={{
                  required: "Waktu mulai harus diisi!",
                }}
                render={({ field }) => (
                  <TimePicker
                    disableClock={true}
                    format="HH:mm"
                    onChange={field.onChange}
                    value={field.value}
                  />
                )}
              />
            </div>
            <div className="flex-1 flex w-full flex-col gap-3">
              <InputField
                extra="mb-3 flex-1 w-full"
                label="Tanggal dan Waktu Berakhir"
                placeholder="Masukkan tanggal berakhir"
                id="end_at"
                type="date"
                variant="outline"
                register={register}
                errors={errors}
                validationSchema={{
                  required: "Tanggal berakhir harus diisi!",
                }}
              />
              <Controller
                name="timeend_at"
                control={control}
                defaultValue={defaultTime}
                rules={{
                  required: "Waktu berakhir harus diisi!",
                }}
                render={({ field }) => (
                  <TimePicker
                    disableClock={true}
                    format="HH:mm"
                    onChange={field.onChange}
                    value={field.value}
                  />
                )}
              />
            </div>
          </div>
          <TextField
            extra="mb-3 flex-1"
            label="Deskripsi"
            placeholder="Deskripsi kupon"
            id="description"
            variant="outline"
            register={register}
            errors={errors}
            validationSchema={{}}
          />
        </div>
        <Button
          children="Simpan"
          size="fit"
          variant="primary"
          extra="mt-7 mb-5"
          isLoading={isLoading}
        />
      </form>
    </Card>
  );
};

export default CouponForm;
